import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiRequest } from '../utils/api';
import {
  GROUP_LABELS, GROUP_COLORS,
  ADMIN_ASSIGNABLE_GROUPS, ALL_GROUPS,
  isSuperAdmin,
} from '../utils/groups';
import { Button, Input, Modal, Badge, AlertBanner, GroupBadge, PageHeader } from './ui';
import PipelinesTab from './PipelinesTab';

const TEMPLATE_TYPES = [
  { id: 'invite',          label: 'Invitation',        description: 'Sent when an admin creates a new user' },
  { id: 'verification',    label: 'Email Verification', description: 'Sent when a user changes their email' },
  { id: 'forgot_password', label: 'Forgot Password',   description: 'Sent when a user requests a password reset' },
];
const TABS = [
  { id: 'users',     label: 'Users' },
  { id: 'templates', label: 'Email Templates' },
  { id: 'pipelines', label: 'Pipelines' },
];
const TAB_STORAGE_KEY = 'adminPanel:activeTab';

// ── Shared input/label styles ─────────────────────────────────────────────────
const inputClass = [
  'w-full px-3.5 py-2.5 rounded-[var(--radius-lg)]',
  'border border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-primary)]',
  'text-sm placeholder-[var(--color-text-muted)]',
  'focus:outline-none focus:ring-2 focus:ring-[var(--color-orange)]/20 focus:border-[var(--color-orange)]',
  'transition-colors',
].join(' ');

const labelClass = 'text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider';

// ── Users tab ─────────────────────────────────────────────────────────────────
function UsersTab({ callerGroups }) {
  const [users,        setUsers]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [search,       setSearch]       = useState('');
  const [showInvite,   setShowInvite]   = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalType,    setModalType]    = useState(null);
  const superAdmin = isSuperAdmin(callerGroups);

  useEffect(() => { loadUsers(); }, []);

  async function loadUsers() {
    setLoading(true); setError(null);
    try { const data = await apiRequest('/admin/users'); setUsers(data.users); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  const filtered = users.filter(u =>
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    `${u.given_name} ${u.family_name}`.toLowerCase().includes(search.toLowerCase())
  );

  function openModal(user, type) { setSelectedUser(user); setModalType(type); }
  function closeModal() { setSelectedUser(null); setModalType(null); }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-xs">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]">
            <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
          </svg>
          <input
            type="text"
            placeholder="Search users..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 h-9 text-sm border border-[var(--color-border)] rounded-[var(--radius-lg)] bg-white focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] focus:border-[var(--color-orange)] transition-colors text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)]"
          />
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowInvite(true)}
          icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>}
        >
          Invite User
        </Button>
      </div>

      {error && <AlertBanner type="error">{error}</AlertBanner>}

      <div className="rounded-[var(--radius-xl)] border border-[var(--color-border)] overflow-hidden shadow-[var(--shadow-xs)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--color-surface)] border-b border-[var(--color-border)]">
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">User</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">Groups</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">Status</th>
              {superAdmin && <th className="text-right px-4 py-3 text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {loading ? [...Array(5)].map((_, i) => (
              <tr key={i}>
                <td className="px-4 py-3"><div className="h-4 w-48 bg-[var(--color-surface-alt)] rounded animate-pulse" /></td>
                <td className="px-4 py-3"><div className="h-4 w-32 bg-[var(--color-surface-alt)] rounded animate-pulse" /></td>
                <td className="px-4 py-3"><div className="h-4 w-16 bg-[var(--color-surface-alt)] rounded animate-pulse" /></td>
                {superAdmin && <td />}
              </tr>
            )) : filtered.length === 0 ? (
              <tr><td colSpan={superAdmin ? 4 : 3} className="px-4 py-8 text-center text-sm text-[var(--color-text-muted)]">
                {search ? 'No users match your search.' : 'No users found.'}
              </td></tr>
            ) : filtered.map(user => (
              <tr key={user.username} className="hover:bg-[var(--color-surface)] transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-[var(--radius-md)] bg-[var(--color-orange)] flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {user.given_name ? user.given_name[0].toUpperCase() : user.email[0].toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-[var(--color-text-primary)] text-sm">
                        {user.given_name || user.family_name ? `${user.given_name} ${user.family_name}`.trim() : '—'}
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)]">{user.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {user.groups.length === 0
                      ? <span className="text-[var(--color-text-muted)] text-xs">No groups</span>
                      : user.groups.map(g => (
                          <GroupBadge key={g} group={g} groupLabels={GROUP_LABELS} groupColors={GROUP_COLORS} />
                        ))
                    }
                  </div>
                </td>
                <td className="px-4 py-3">
                  <Badge variant={user.enabled ? 'success' : 'default'} dot>
                    {user.enabled ? 'Active' : 'Disabled'}
                  </Badge>
                </td>
                {superAdmin && (
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openModal(user, 'groups')} className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] px-2.5 py-1.5 rounded-[var(--radius-md)] hover:bg-[var(--color-surface-alt)] transition-colors font-medium">Edit Groups</button>
                      <button onClick={() => openModal(user, 'reset')} className="text-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] px-2.5 py-1.5 rounded-[var(--radius-md)] hover:bg-[var(--color-surface-alt)] transition-colors font-medium">Reset PW</button>
                      <button
                        onClick={() => openModal(user, 'status')}
                        className={`text-xs px-2.5 py-1.5 rounded-[var(--radius-md)] transition-colors font-medium ${user.enabled ? 'text-[var(--color-error)] hover:bg-[var(--color-error-bg)]' : 'text-[var(--color-success)] hover:bg-[var(--color-success-bg)]'}`}
                      >
                        {user.enabled ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showInvite && <InviteModal callerGroups={callerGroups} onClose={() => setShowInvite(false)} onSuccess={() => { setShowInvite(false); loadUsers(); }} />}
      {selectedUser && modalType === 'groups'  && <EditGroupsModal    user={selectedUser} onClose={closeModal} onSuccess={() => { closeModal(); loadUsers(); }} />}
      {selectedUser && modalType === 'reset'   && <ResetPasswordModal user={selectedUser} onClose={closeModal} />}
      {selectedUser && modalType === 'status'  && <ToggleStatusModal  user={selectedUser} onClose={closeModal} onSuccess={() => { closeModal(); loadUsers(); }} />}
    </div>
  );
}

// ── Invite modal ──────────────────────────────────────────────────────────────
function InviteModal({ callerGroups, onClose, onSuccess }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [group, setGroup] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const availableGroups = isSuperAdmin(callerGroups) ? ALL_GROUPS : ADMIN_ASSIGNABLE_GROUPS;

  async function handleInvite() {
    if (!email || !group) { setStatus({ type: 'error', message: 'Email and group are required.' }); return; }
    setLoading(true); setStatus(null);
    try {
      await apiRequest('/admin/users', { method: 'POST', body: JSON.stringify({ email, given_name: firstName, family_name: lastName, group }) });
      onSuccess();
    } catch (err) { setStatus({ type: 'error', message: err.message }); }
    finally { setLoading(false); }
  }

  return (
    <Modal open title="Invite New User" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5"><label className={labelClass}>First Name</label><input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="First name" className={inputClass} /></div>
        <div className="flex flex-col gap-1.5"><label className={labelClass}>Last Name</label><input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Last name" className={inputClass} /></div>
      </div>
      <div className="flex flex-col gap-1.5"><label className={labelClass}>Email Address</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="netid@illinois.edu" className={inputClass} /></div>
      <div className="flex flex-col gap-1.5">
        <label className={labelClass}>Assign Group</label>
        <select value={group} onChange={e => setGroup(e.target.value)} className={inputClass}>
          <option value="">Select a group...</option>
          {availableGroups.map(g => <option key={g} value={g}>{GROUP_LABELS[g] || g}</option>)}
        </select>
      </div>
      <p className="text-xs text-[var(--color-text-muted)] leading-relaxed">The user will receive a temporary password and be prompted to set a new one on first login.</p>
      {status && <AlertBanner type={status.type}>{status.message}</AlertBanner>}
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={loading} onClick={handleInvite}>{loading ? 'Sending invite...' : 'Send Invitation'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Edit groups modal ─────────────────────────────────────────────────────────
function EditGroupsModal({ user, onClose, onSuccess }) {
  const [selectedGroups, setSelectedGroups] = useState(new Set(user.groups));
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  function toggleGroup(g) {
    setSelectedGroups(prev => { const next = new Set(prev); next.has(g) ? next.delete(g) : next.add(g); return next; });
  }

  async function handleSave() {
    setLoading(true); setStatus(null);
    const current  = new Set(user.groups);
    const toAdd    = [...selectedGroups].filter(g => !current.has(g));
    const toRemove = [...current].filter(g => !selectedGroups.has(g));
    try {
      await apiRequest(`/admin/users/${encodeURIComponent(user.username)}/groups`, { method: 'PUT', body: JSON.stringify({ add: toAdd, remove: toRemove }) });
      onSuccess();
    } catch (err) { setStatus({ type: 'error', message: err.message }); }
    finally { setLoading(false); }
  }

  return (
    <Modal open title={`Edit Groups`} description={user.email} onClose={onClose}>
      <p className="text-xs text-[var(--color-text-muted)]">Select all groups this user should belong to.</p>
      <div className="flex flex-wrap gap-2">
        {ALL_GROUPS.map(g => (
          <button
            key={g}
            onClick={() => toggleGroup(g)}
            className={[
              'px-3 py-1.5 rounded-[var(--radius-lg)] text-xs font-medium border transition-colors',
              selectedGroups.has(g)
                ? `${GROUP_COLORS[g] || 'bg-[var(--color-orange-light)] text-[var(--color-orange)] border-[var(--color-orange)]'}`
                : 'bg-[var(--color-surface)] text-[var(--color-text-muted)] border-[var(--color-border)] hover:border-[var(--color-border-strong)]',
            ].join(' ')}
          >
            {GROUP_LABELS[g] || g}
          </button>
        ))}
      </div>
      {status && <AlertBanner type={status.type}>{status.message}</AlertBanner>}
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={loading} onClick={handleSave}>{loading ? 'Saving...' : 'Save Groups'}</Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Reset password modal ──────────────────────────────────────────────────────
function ResetPasswordModal({ user, onClose }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  async function handleReset() {
    setLoading(true); setStatus(null);
    try {
      await apiRequest(`/admin/users/${encodeURIComponent(user.username)}/password`, { method: 'POST' });
      setStatus({ type: 'success', message: 'Password reset email sent successfully.' });
    } catch (err) { setStatus({ type: 'error', message: err.message }); }
    finally { setLoading(false); }
  }

  return (
    <Modal open title="Reset Password" onClose={onClose}>
      <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
        This will send a password reset email to <span className="font-medium text-[var(--color-text-primary)]">{user.email}</span>.
      </p>
      {status && <AlertBanner type={status.type}>{status.message}</AlertBanner>}
      <Modal.Footer>
        {status?.type !== 'success' ? (
          <>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button variant="primary" loading={loading} onClick={handleReset}>{loading ? 'Sending...' : 'Send Reset Email'}</Button>
          </>
        ) : (
          <Button variant="secondary" fullWidth onClick={onClose}>Close</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

// ── Toggle status modal ───────────────────────────────────────────────────────
function ToggleStatusModal({ user, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const enabling = !user.enabled;

  async function handleToggle() {
    setLoading(true); setStatus(null);
    try {
      await apiRequest(`/admin/users/${encodeURIComponent(user.username)}/status`, { method: 'PUT', body: JSON.stringify({ enabled: enabling }) });
      onSuccess();
    } catch (err) { setStatus({ type: 'error', message: err.message }); }
    finally { setLoading(false); }
  }

  return (
    <Modal open title={enabling ? 'Enable User' : 'Disable User'} onClose={onClose}>
      <p className="text-sm text-[var(--color-text-secondary)] leading-relaxed">
        {enabling
          ? <>Are you sure you want to enable <span className="font-medium text-[var(--color-text-primary)]">{user.email}</span>? They will be able to sign in again.</>
          : <>Are you sure you want to disable <span className="font-medium text-[var(--color-text-primary)]">{user.email}</span>? They will no longer be able to sign in.</>
        }
      </p>
      {status && <AlertBanner type={status.type}>{status.message}</AlertBanner>}
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant={enabling ? 'primary' : 'danger'} loading={loading} onClick={handleToggle}>
          {loading ? 'Saving...' : enabling ? 'Enable User' : 'Disable User'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ── Templates tab ─────────────────────────────────────────────────────────────
function TemplatesTab() {
  const [templates,      setTemplates]      = useState({});
  const [loading,        setLoading]        = useState(true);
  const [activeTemplate, setActiveTemplate] = useState('invite');
  const [subject,        setSubject]        = useState('');
  const [body,           setBody]           = useState('');
  const [saving,         setSaving]         = useState(false);
  const [status,         setStatus]         = useState(null);
  const [preview,        setPreview]        = useState(false);
  const prevTemplateRef = React.useRef(activeTemplate);

  useEffect(() => { loadTemplates(); }, []);

  useEffect(() => {
    if (!templates[activeTemplate]) return;
    const switched = prevTemplateRef.current !== activeTemplate;
    prevTemplateRef.current = activeTemplate;
    setSubject(templates[activeTemplate].subject || '');
    setBody(templates[activeTemplate].body || '');
    setPreview(false);
    if (switched) setStatus(null);
  }, [activeTemplate, templates]);

  async function loadTemplates() {
    setLoading(true);
    try {
      const data = await apiRequest('/admin/templates');
      setTemplates(data.templates);
      const first = data.templates['invite'];
      if (first) { setSubject(first.subject || ''); setBody(first.body || ''); }
    } catch (err) { setStatus({ type: 'error', message: err.message }); }
    finally { setLoading(false); }
  }

  async function handleSave() {
    setSaving(true); setStatus(null);
    try {
      await apiRequest(`/admin/templates/${activeTemplate}`, { method: 'PUT', body: JSON.stringify({ subject, body }) });
      setTemplates(prev => ({ ...prev, [activeTemplate]: { subject, body } }));
      setStatus({ type: 'success', message: 'Template saved successfully.' });
    } catch (err) { setStatus({ type: 'error', message: err.message }); }
    finally { setSaving(false); }
  }

  if (loading) return (
    <div className="space-y-3">
      {[...Array(3)].map((_, i) => <div key={i} className="h-10 rounded-[var(--radius-lg)] bg-[var(--color-surface-alt)] animate-pulse" />)}
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex gap-2 flex-wrap">
        {TEMPLATE_TYPES.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTemplate(t.id)}
            className={[
              'px-4 h-9 rounded-[var(--radius-lg)] text-sm font-medium border transition-colors',
              activeTemplate === t.id
                ? 'bg-[var(--color-orange)] text-white border-[var(--color-orange)]'
                : 'bg-white text-[var(--color-text-muted)] border-[var(--color-border)] hover:border-[var(--color-border-strong)] hover:text-[var(--color-text-primary)]',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-[var(--color-text-muted)]">
        {TEMPLATE_TYPES.find(t => t.id === activeTemplate)?.description}
      </p>
      <div className="flex flex-col gap-1.5">
        <label className={labelClass}>Subject Line</label>
        <input type="text" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject..." className={inputClass} />
      </div>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <label className={labelClass}>HTML Body</label>
          <button
            onClick={() => setPreview(v => !v)}
            className="text-xs text-[var(--color-orange)] hover:text-[var(--color-orange-hover)] font-medium transition-colors"
          >
            {preview ? 'Edit HTML' : 'Preview'}
          </button>
        </div>
        {preview
          ? <div className="w-full min-h-[280px] rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-white p-4 text-sm overflow-auto" dangerouslySetInnerHTML={{ __html: body }} />
          : <textarea value={body} onChange={e => setBody(e.target.value)} rows={12} placeholder="<html>...</html>" className={`${inputClass} font-mono text-xs resize-y`} />
        }
        <p className="text-[11px] text-[var(--color-text-muted)]">
          Use <code className="bg-[var(--color-surface-alt)] px-1 rounded text-[10px]">{'{{username}}'}</code> and <code className="bg-[var(--color-surface-alt)] px-1 rounded text-[10px]">{'{{####}}'}</code> as Cognito placeholders.
        </p>
      </div>
      {status && <AlertBanner type={status.type}>{status.message}</AlertBanner>}
      <Button variant="primary" loading={saving} onClick={handleSave}>{saving ? 'Saving...' : 'Save Template'}</Button>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function AdminPanel() {
  const [callerGroups,  setCallerGroups]  = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [activeTab,     setActiveTab]     = useState(() => {
    try { return localStorage.getItem(TAB_STORAGE_KEY) || 'users'; } catch { return 'users'; }
  });

  useEffect(() => {
    fetchAuthSession()
      .then(session => {
        const payload = session.tokens?.idToken?.payload || {};
        const raw = payload['cognito:groups'] || [];
        setCallerGroups(Array.isArray(raw) ? raw : [raw]);
      })
      .catch(console.error)
      .finally(() => setLoadingGroups(false));
  }, []);

  function handleTabChange(tabId) {
    setActiveTab(tabId);
    try { localStorage.setItem(TAB_STORAGE_KEY, tabId); } catch {}
  }

  const superAdmin = isSuperAdmin(callerGroups);
  if (loadingGroups) return null;

  // Templates and Pipelines are superadmin-only — infrastructure-level controls
  const visibleTabs = TABS.filter(t =>
    t.id === 'users' ||
    (t.id === 'templates' && superAdmin) ||
    (t.id === 'pipelines' && superAdmin)
  );

  return (
    <div className="h-full flex flex-col overflow-hidden bg-white">

      <PageHeader
        title="Admin Panel"
        subtitle={superAdmin ? 'Manage users, groups, email templates, and pipeline observability.' : 'Invite users and manage basic group assignments.'}
      />

      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        {/* Tab bar */}
        <div className="flex border-b border-[var(--color-border)] px-6 sm:px-8 bg-white shrink-0">
          {visibleTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={[
                'px-4 py-3.5 text-sm font-medium transition-colors duration-[var(--transition-base)]',
                'border-b-2 -mb-px',
                activeTab === tab.id
                  ? 'text-[var(--color-orange)] border-[var(--color-orange)]'
                  : 'text-[var(--color-text-muted)] border-transparent hover:text-[var(--color-text-secondary)]',
              ].join(' ')}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6 sm:p-8">
          {activeTab === 'users'     && <UsersTab callerGroups={callerGroups} />}
          {activeTab === 'templates' && superAdmin && <TemplatesTab />}
          {activeTab === 'pipelines' && superAdmin && <PipelinesTab />}
        </div>
      </div>
    </div>
  );
}